import { css as _css } from "@emotion/core";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from "react";
import PropTypes from "prop-types";
import useTheme from "../hooks/useTheme";
import { BackgroundProvider } from "../hooks/useBackground";
import { responsiveMarginType, responsivePaddingType, responsivePropType } from "../hooks/useResponsiveProp";
import useResponsivePropsCSS from "../hooks/useResponsivePropsCSS";
import { mergeProps } from "../utils/component";
import { responsiveMargin, responsivePadding } from "../utils/css";
import { jsx as ___EmotionJSX } from "@emotion/core";
var sizesMap = {
  tiny: {
    offset: 20,
    borderWidth: 20,
    minSize: 60
  },
  small: {
    offset: 40,
    borderWidth: 24,
    minSize: 104
  },
  medium: {
    offset: 48,
    borderWidth: 32,
    minSize: 128
  },
  large: {
    offset: 60,
    borderWidth: 40,
    minSize: 160
  }
};
var colorsMap = {
  low: {
    grey: {
      contentBackgroundColor: "grey.t03",
      shadowBorderColor: "grey.t07"
    },
    blue: {
      contentBackgroundColor: "secondary.lightBlue.t25",
      shadowBorderColor: "secondary.lightBlue.t15"
    },
    pink: {
      contentBackgroundColor: "secondary.pink.t30",
      shadowBorderColor: "secondary.pink.t15"
    },
    purple: {
      contentBackgroundColor: "secondary.purple.t30",
      shadowBorderColor: "secondary.purple.t15"
    }
  },
  medium: {
    grey: {
      contentBackgroundColor: "white",
      shadowBorderColor: "grey.t07"
    },
    blue: {
      contentBackgroundColor: "secondary.lightBlue.t25",
      shadowBorderColor: "secondary.lightBlue.t60"
    },
    pink: {
      contentBackgroundColor: "secondary.pink.t30",
      shadowBorderColor: "secondary.pink.t60"
    },
    purple: {
      contentBackgroundColor: "secondary.purple.t30",
      shadowBorderColor: "secondary.purple.t60"
    }
  },
  high: {
    grey: {
      contentBackgroundColor: "white",
      shadowBorderColor: "grey.t16"
    },
    blue: {
      contentBackgroundColor: "secondary.lightBlue.t60",
      shadowBorderColor: "secondary.lightBlue.t100"
    },
    pink: {
      contentBackgroundColor: "secondary.pink.t60",
      shadowBorderColor: "secondary.pink.t100"
    },
    purple: {
      contentBackgroundColor: "secondary.purple.t60",
      shadowBorderColor: "secondary.purple.t100"
    }
  }
};

function getColors(backgroundVariant, shadowColor) {
  var _colorsMap$background = colorsMap[backgroundVariant][shadowColor],
      contentBackgroundColor = _colorsMap$background.contentBackgroundColor,
      shadowBorderColor = _colorsMap$background.shadowBorderColor;
  return {
    contentBackgroundColor: contentBackgroundColor,
    shadowBorderColor: shadowBorderColor
  };
}

var SHADOW_SIZES = ["none", "tiny", "small", "medium", "large"];
var SHADOW_DIRECTIONS = ["left", "right"];
var SHADOW_COLORS = ["grey", "blue", "pink", "purple"];
var SHADOW_CONTRASTS = ["low", "medium", "high"];
var DEFAULT_PROPS = {
  shadowSize: "large",
  shadowDirection: "right",
  shadowColor: "grey",
  shadowContrast: "high"
};
ShadowContainer.SHADOW_SIZES = SHADOW_SIZES;
ShadowContainer.SHADOW_DIRECTIONS = SHADOW_DIRECTIONS;
ShadowContainer.SHADOW_COLORS = SHADOW_COLORS;
ShadowContainer.SHADOW_CONTRASTS = SHADOW_CONTRASTS;
ShadowContainer.DEFAULT_PROPS = DEFAULT_PROPS;

function ShadowContainer(props) {
  var theme = useTheme();
  var mergedProps = mergeProps(props, DEFAULT_PROPS, {}, {
    shadowSize: function shadowSize(_shadowSize) {
      return SHADOW_SIZES.includes(_shadowSize);
    },
    shadowDirection: function shadowDirection(_shadowDirection) {
      return SHADOW_DIRECTIONS.includes(_shadowDirection);
    },
    shadowColor: function shadowColor(_shadowColor) {
      return SHADOW_COLORS.includes(_shadowColor);
    },
    shadowContrast: function shadowContrast(_shadowContrast) {
      return SHADOW_CONTRASTS.includes(_shadowContrast);
    }
  });
  var shadowDirection = mergedProps.shadowDirection,
      shadowColor = mergedProps.shadowColor,
      shadowContrast = mergedProps.shadowContrast,
      children = mergedProps.children,
      testId = mergedProps.testId;

  var _getColors = getColors(shadowContrast, shadowColor),
      contentBackgroundColor = _getColors.contentBackgroundColor,
      shadowBorderColor = _getColors.shadowBorderColor;

  var contentHasBorder = shadowColor === "grey";
  var contentBorderWidth = contentHasBorder ? parseInt(theme.borderWidths[0], 10) : 0;
  var contentBorderColor = theme.colors.grey.t10;
  var shadowSizePx = "calc(100% + ".concat(2 * contentBorderWidth, "px)");
  var contentResponsiveCSS = useResponsivePropsCSS(props, DEFAULT_PROPS, {
    margin: responsiveMargin,
    padding: responsivePadding,
    shadowSize: function shadowSize(_ref) {
      var _shadowSize2 = _ref.shadowSize;

      if (_shadowSize2 === "none") {
        return {};
      }

      var contentMinSize = sizesMap[_shadowSize2].minSize;
      return {
        minWidth: contentMinSize,
        minHeight: contentMinSize
      };
    }
  });
  var contentBeforeResponsiveCSS = useResponsivePropsCSS(props, DEFAULT_PROPS, {
    shadowSize: function shadowSize(_ref2) {
      var _shadowSize3 = _ref2.shadowSize;
      var display = _shadowSize3 === "none" ? "none" : "block";

      if (_shadowSize3 === "none") {
        return {
          display: display
        };
      }

      var shadowOffset = sizesMap[_shadowSize3].offset;
      var shadowBorderWidth = sizesMap[_shadowSize3].borderWidth;
      return {
        display: display,
        top: shadowOffset - contentBorderWidth,
        left: (shadowDirection === "left" ? -shadowOffset : shadowOffset) - contentBorderWidth,
        border: "".concat(shadowBorderWidth, "px solid ").concat(theme.getColor(shadowBorderColor))
      };
    }
  });
  return ___EmotionJSX(BackgroundProvider, {
    value: contentBackgroundColor
  }, ___EmotionJSX("div", {
    css: /*#__PURE__*/_css(_objectSpread(_objectSpread(_objectSpread({
      /* 
        We set { display: "flex", flexDirection: "column" } in order to allow `children`
        to be full height.
        See: https://stackoverflow.com/q/8468066/247243
      */
      display: "flex",
      flexDirection: "column",
      boxSizing: "border-box"
    }, contentResponsiveCSS), {}, {
      backgroundColor: theme.getColor(contentBackgroundColor)
    }, contentHasBorder && {
      border: "".concat(contentBorderWidth, "px solid ").concat(contentBorderColor)
    }), {}, {
      position: "relative",
      transformStyle: "preserve-3d",
      // See: https://stackoverflow.com/a/51432213/247243
      "::before": _objectSpread({
        transform: "translateZ(-1px)",
        // See: https://stackoverflow.com/a/51432213/247243
        boxSizing: "border-box",
        content: '""',
        position: "absolute",
        width: shadowSizePx,
        height: shadowSizePx
      }, contentBeforeResponsiveCSS)
    }), process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"),
    "data-testid": testId
  }, children));
}

ShadowContainer.propTypes = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, responsiveMarginType), responsivePaddingType), responsivePropType("shadowSize", PropTypes.oneOf(SHADOW_SIZES))), {}, {
  shadowDirection: PropTypes.oneOf(SHADOW_DIRECTIONS),
  shadowColor: PropTypes.oneOf(SHADOW_COLORS),
  shadowContrast: PropTypes.oneOf(SHADOW_CONTRASTS),
  children: PropTypes.node,
  testId: PropTypes.string
});
export default ShadowContainer;